import { Icon } from "@/components/Icon";
import "./PaymentAnomalyNotice.css";

interface PaymentAnomalyNoticeProps {
  /** Label of the card/instrument actually used on this entry, e.g. "Amex ··1007". */
  attemptedLabel: string;
  /** Labels of the instrument(s) this vendor has been paid with before. */
  previouslyUsedLabels: string[];
  /**
   * `banner` — the phone success screen, full-width with explanatory copy.
   * `badge`  — the laptop dashboard row, compact, same information.
   */
  variant?: "banner" | "badge";
}

/**
 * The payoff of the receipt-mapping demo's third beat: *"you paid this one
 * on a different card than usual — did you mean to?"*
 *
 * DELIBERATELY NOT AN ERROR STATE. Nothing failed and the entry saved —
 * `role="status"` (a polite live-region), not `role="alert"`, and no red.
 * Gold is used only as a fill/badge background per docs/brand/README.md
 * ("gold is not a text color on white"); all text here uses the standard
 * `--color-text` pair, which is what keeps it reading as a friendly
 * heads-up instead of a warning.
 */
export function PaymentAnomalyNotice({
  attemptedLabel,
  previouslyUsedLabels,
  variant = "banner",
}: PaymentAnomalyNoticeProps) {
  const usualLabel = previouslyUsedLabels.join(" or ");

  return (
    <div className={`payment-anomaly payment-anomaly--${variant}`} role="status">
      <span className="payment-anomaly__icon" aria-hidden="true">
        <Icon name="card" size={variant === "badge" ? 14 : 18} />
      </span>
      <span className="payment-anomaly__text">
        <strong className="payment-anomaly__headline">Different card than usual</strong>
        {variant === "banner" ? (
          <span className="payment-anomaly__detail">
            Paid with <strong>{attemptedLabel}</strong> this time — usually{" "}
            <strong>{usualLabel}</strong>. Did you mean to?
          </span>
        ) : (
          <span className="payment-anomaly__detail">
            {attemptedLabel} instead of the usual {usualLabel}
          </span>
        )}
      </span>
    </div>
  );
}
